import React, { useCallback, useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
    ArrowLeft, Building2, Ruler, Home, BookOpen, Landmark, TrendingUp, Pencil, User,
} from "lucide-react";
import {
    getUnit, getProperty, listTenants, listTurnovers, mietverlauf, updateTenant,
} from "@/lib/api";
import { germanDate, euro, statusLabel, istAktivesMietverhaeltnis } from "@/lib/format";
import { useSchreibschutz } from "@/lib/auth";
import StatusBadge from "@/components/StatusBadge";
import MietanpassungDialog from "@/components/MietanpassungDialog";
import WikiPanel from "@/components/WikiPanel";
import SuchAuswahl from "@/components/ui/such-auswahl";
import { toast } from "sonner";
import { Seitenkopf } from "@/hilfe/Seitenkopf";
import { sperre } from "@/hilfe/Knopfhilfe";
import { Hilfe } from "@/hilfe/Hilfe";

const KV = ({ icon: Icon, label, value }) => (
    <div className="flex items-start gap-3 py-3 border-b border-gray-100">
        {Icon && <Icon size={14} className="text-gray-400 mt-0.5" />}
        <div className="flex-1">
            <div className="text-[11px] uppercase tracking-[0.18em] text-gray-500">{label}</div>
            <div className="text-sm text-slate-900 mt-0.5">{value ?? "—"}</div>
        </div>
    </div>
);

// Das Soll eines Mietverhaeltnisses -- dieselbe Rechnung wie in der
// Mieterliste. Kalt + NK + Heizung, oder die Pauschale.
const sollAus = (t) => (t?.mietart === "pauschal"
    ? (t?.pauschalmiete || 0)
    : (t?.kaltmiete || 0) + (t?.nebenkosten || 0) + (t?.heizkosten || 0));

// ---------------------------------------------------------------------------
// Vertragsmiete bearbeiten -- ohne den Umweg ueber die Mieterliste
// ---------------------------------------------------------------------------
// Bewusst nur die Geldfelder: wer hier ist, will den Betrag korrigieren, nicht
// den Namen aendern. Fuer eine Erhoehung ab einem Stichtag ist "Miete anpassen"
// der richtige Weg -- das steht auch im Dialog, sonst wird der eine Weg fuer
// den anderen benutzt und der Verlauf bleibt leer.
const MieteDialog = ({ mieter, open, onOpenChange, onDone }) => {
    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open || !mieter) return;
        setForm({
            mietart: mieter.mietart || "kalt_nk",
            kaltmiete: mieter.kaltmiete ?? "",
            nebenkosten: mieter.nebenkosten ?? "",
            heizkosten: mieter.heizkosten ?? "",
            pauschalmiete: mieter.pauschalmiete ?? "",
            kaution: mieter.kaution ?? "",
        });
    }, [open, mieter]);

    const zahl = (v) => (v === "" || v === null || v === undefined ? null : parseFloat(v));

    const speichern = async () => {
        setSaving(true);
        try {
            // PUT ersetzt den ganzen Datensatz: erst den geladenen Mieter
            // nehmen, dann die Geldfelder darueberlegen. Die Version wandert
            // in lib/api in den If-Match-Kopf.
            await updateTenant(mieter.id, {
                ...mieter,
                mietart: form.mietart,
                kaltmiete: zahl(form.kaltmiete),
                nebenkosten: zahl(form.nebenkosten),
                heizkosten: zahl(form.heizkosten),
                pauschalmiete: zahl(form.pauschalmiete),
                kaution: zahl(form.kaution),
            });
            toast.success("Miete gespeichert");
            onOpenChange(false);
            onDone?.();
        } finally { setSaving(false); }
    };

    const pauschal = form.mietart === "pauschal";
    const summe = pauschal
        ? (parseFloat(form.pauschalmiete) || 0)
        : (parseFloat(form.kaltmiete) || 0) + (parseFloat(form.nebenkosten) || 0)
          + (parseFloat(form.heizkosten) || 0);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle>Miete bearbeiten — {mieter?.full_name}</DialogTitle>
                    <DialogDescription>
                        Ändert den Betrag am Mietverhältnis. Bereits erzeugte Soll-Positionen
                        bleiben, wie sie sind. Für eine Erhöhung ab einem Stichtag ist
                        „Miete anpassen" der richtige Weg — nur die landet im Mietverlauf.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-3 py-1">
                    <div>
                        <Label className="text-xs text-gray-500">Mietart<Hilfe id="mietart" /></Label>
                        <SuchAuswahl wert={form.mietart || "kalt_nk"} onWaehlen={(v) => setForm({ ...form, mietart: v })}
                            optionen={[
                                { wert: "kalt_nk", text: "Kalt + Nebenkosten" },
                                { wert: "pauschal", text: "Pauschalmiete" },
                            ]}
                            testId="miete-mietart"
                        />
                    </div>

                    {pauschal ? (
                        <div>
                            <Label className="text-xs text-gray-500">Pauschalmiete<Hilfe id="mietbetrag" /></Label>
                            <Input type="number" step="0.01" value={form.pauschalmiete ?? ""}
                                   data-testid="miete-pauschal"
                                   onChange={(e) => setForm({ ...form, pauschalmiete: e.target.value })} />
                        </div>
                    ) : (
                        <div className="grid grid-cols-3 gap-2">
                            <div>
                                <Label className="text-xs text-gray-500">Kaltmiete<Hilfe id="mietbetrag" /></Label>
                                <Input type="number" step="0.01" value={form.kaltmiete ?? ""}
                                       data-testid="miete-kalt"
                                       onChange={(e) => setForm({ ...form, kaltmiete: e.target.value })} />
                            </div>
                            <div>
                                <Label className="text-xs text-gray-500">Nebenkosten<Hilfe id="mietbetrag" /></Label>
                                <Input type="number" step="0.01" value={form.nebenkosten ?? ""}
                                       data-testid="miete-nk"
                                       onChange={(e) => setForm({ ...form, nebenkosten: e.target.value })} />
                            </div>
                            <div>
                                <Label className="text-xs text-gray-500">Heizkosten<Hilfe id="mietbetrag" /></Label>
                                <Input type="number" step="0.01" value={form.heizkosten ?? ""}
                                       data-testid="miete-heiz"
                                       onChange={(e) => setForm({ ...form, heizkosten: e.target.value })} />
                            </div>
                        </div>
                    )}

                    <div className="flex items-center justify-between border-t border-gray-100 pt-2 text-sm">
                        <span className="text-gray-500">Warmmiete gesamt</span>
                        <b className="tabular-nums" data-testid="miete-summe">{euro(summe)}</b>
                    </div>

                    <div>
                        <Label className="text-xs text-gray-500">Kaution</Label>
                        <Input type="number" step="0.01" value={form.kaution ?? ""}
                               data-testid="miete-kaution"
                               onChange={(e) => setForm({ ...form, kaution: e.target.value })} />
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
                    <Button onClick={speichern} disabled={saving}
                            grund={sperre([saving, "Wird gespeichert"])}
                            className="bg-slate-900 hover:bg-slate-800" data-testid="miete-speichern">
                        {saving ? "Speichere…" : "Speichern"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

// ---------------------------------------------------------------------------
// Wer wohnt hier, was zahlt er, und was ist daran zu tun
// ---------------------------------------------------------------------------
// Bis zum 10.09.2026 stand auf dieser Seite nur eine Mieterhistorie -- wer die
// Miete aendern wollte, musste in die Mieterliste wechseln und die Zeile dort
// suchen. Der aktuelle Mieter und seine Miete gehoeren an die Einheit.
const Mietverhaeltnis = ({ mieter, verlauf, gesperrt, grund, onAnpassen, onBearbeiten }) => {
    if (!mieter) {
        return (
            <div className="bg-white border border-gray-200 rounded-md px-5 py-6 text-sm text-gray-400"
                 data-testid="unit-kein-mieter">
                Aktuell kein laufendes Mietverhältnis an dieser Einheit.
            </div>
        );
    }

    const heute = verlauf?.heute;
    const vertrag = sollAus(mieter);
    const gilt = heute?.soll_total ?? vertrag;
    const ausAnpassung = heute && heute.quelle && heute.quelle !== "mietverhaeltnis";
    const anlass = verlauf?.anlaesse?.[heute?.anlass] || heute?.anlass;

    return (
        <div className="bg-white border border-gray-200 rounded-md" data-testid="unit-mietverhaeltnis">
            <div className="flex items-start justify-between gap-4 px-5 py-4 border-b border-gray-200">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.18em] text-gray-500 mb-1.5">
                        Aktuelles Mietverhältnis
                    </div>
                    <div className="flex items-center gap-2">
                        <User size={15} className="text-gray-400" />
                        <span className="font-heading font-semibold text-slate-900 text-lg"
                              data-testid="unit-mieter-name">{mieter.full_name}</span>
                        <StatusBadge status={mieter.status} />
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                        Einzug {germanDate(mieter.einzugsdatum)}
                        {mieter.auszugsdatum && <> · Mietende {germanDate(mieter.auszugsdatum)}</>}
                        {mieter.uebergabe_datum && <> · Übergabe {germanDate(mieter.uebergabe_datum)}</>}
                        {mieter.kaution ? <> · Kaution {euro(mieter.kaution)}</> : null}
                    </div>
                </div>
                <div className="flex flex-wrap justify-end gap-2">
                    <Button size="sm" variant="outline" className="h-8" onClick={onAnpassen}
                            disabled={gesperrt} data-testid="unit-miete-anpassen"
                            grund={grund} hilfe="aktion.createMietanpassung">
                        <TrendingUp size={12} className="mr-1.5" /> Miete anpassen
                    </Button>
                    <Button size="sm" variant="ghost" className="h-8" onClick={onBearbeiten}
                            disabled={gesperrt} data-testid="unit-miete-bearbeiten"
                            grund={grund} title="Vertragsbetrag korrigieren">
                        <Pencil size={12} className="mr-1.5" /> Bearbeiten
                    </Button>
                </div>
            </div>

            <div className="px-5 py-4 grid grid-cols-2 sm:grid-cols-4 gap-4">
                {mieter.mietart === "pauschal" ? (
                    <div>
                        <div className="kachel-label" title="Pauschalmiete">Pauschalmiete</div>
                        <div className="text-sm text-slate-900 tabular-nums mt-0.5">
                            {euro(heute?.pauschalmiete ?? mieter.pauschalmiete)}
                        </div>
                    </div>
                ) : (
                    <>
                        <div>
                            <div className="kachel-label" title="Kaltmiete">Kaltmiete</div>
                            <div className="text-sm text-slate-900 tabular-nums mt-0.5">
                                {euro(heute?.kaltmiete ?? mieter.kaltmiete)}
                            </div>
                        </div>
                        <div>
                            <div className="kachel-label" title="Nebenkosten">Nebenkosten</div>
                            <div className="text-sm text-slate-900 tabular-nums mt-0.5">
                                {euro(heute?.nebenkosten ?? mieter.nebenkosten)}
                            </div>
                        </div>
                        <div>
                            <div className="kachel-label" title="Heizkosten">Heizkosten</div>
                            <div className="text-sm text-slate-900 tabular-nums mt-0.5">
                                {euro(heute?.heizkosten ?? mieter.heizkosten)}
                            </div>
                        </div>
                    </>
                )}
                <div>
                    <div className="kachel-label">Gesamt / Monat<Hilfe id="miete-gesamt" /></div>
                    <div className="text-sm font-semibold text-slate-900 tabular-nums mt-0.5"
                         data-testid="unit-miete-gesamt">{euro(gilt)}</div>
                </div>
            </div>

            {/* Weicht der geltende Betrag vom Vertrag ab, muss dastehen, woher er
                kommt -- sonst sucht man den Unterschied im Mieterdatensatz. */}
            {ausAnpassung && (
                <div className="px-5 pb-4 -mt-1">
                    <div className="text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2"
                         data-testid="unit-miete-quelle">
                        Gilt seit {heute.gilt_ab} aus einer Mietanpassung{anlass ? ` (${anlass})` : ""}.
                        Im Mietverhältnis stehen {euro(vertrag)}.
                    </div>
                </div>
            )}
        </div>
    );
};

const UnitDetail = () => {
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const initialTab = searchParams.get("tab") === "wiki" ? "wiki" : "overview";
    const highlightEntry = searchParams.get("entry") || null;
    const [tab, setTab] = useState(initialTab);
    const [unit, setUnit] = useState(null);
    const [property, setProperty] = useState(null);
    const [tenants, setTenants] = useState([]);
    const [turnovers, setTurnovers] = useState([]);
    const [verlauf, setVerlauf] = useState(null);
    const [anpassungOffen, setAnpassungOffen] = useState(false);
    const [mieteOffen, setMieteOffen] = useState(false);
    const { gesperrt, grund } = useSchreibschutz();

    const load = useCallback(async () => {
        const [u, t, tv] = await Promise.all([
            getUnit(id), listTenants({ unit_id: id }), listTurnovers(),
        ]);
        setUnit(u);
        setTenants(t);
        setTurnovers(tv.filter((x) => x.unit_id === id));
        if (u.property_id) setProperty(await getProperty(u.property_id));

        // Der Verlauf sagt, welcher Betrag HEUTE gilt -- der Mieterdatensatz
        // kennt nur den Vertragswert.
        const cur = t.find((x) => istAktivesMietverhaeltnis(x) && x.status === "aktiv")
            || t.find(istAktivesMietverhaeltnis);
        setVerlauf(cur ? await mietverlauf(cur.id).catch(() => null) : null);
    }, [id]);

    useEffect(() => { load(); }, [load]);

    if (!unit) return <div className="p-10 text-gray-400">Lade…</div>;

    const aktuell = tenants.find((t) => istAktivesMietverhaeltnis(t) && t.status === "aktiv")
        || tenants.find(istAktivesMietverhaeltnis)
        || null;

    return (
        <div className="px-6 md:px-10 py-8 space-y-8">
            <Link to="/einheiten" className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-slate-900">
                <ArrowLeft size={14} /> Zurück zu Mieteinheiten
            </Link>

            <div className="flex items-start justify-between gap-6">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.2em] text-gray-500 mb-2">
                        {statusLabel(unit.typ)}
                    </div>
                    <h1 className="font-heading text-3xl font-bold text-slate-900" data-testid="unit-detail-title">
                        {unit.unit_label || "Einheit"}
                    </h1>
                    <Seitenkopf id="seite.einheit" />
                    {property && (
                        <Link
                            to={`/immobilien/${property.id}`}
                            className="inline-flex items-center gap-1.5 mt-2 text-sm text-slate-600 hover:text-slate-900"
                            data-testid="unit-property-link"
                        >
                            <Building2 size={13} /> {property.label || property.address}
                            {property.city ? ` · ${property.city}` : ""}
                        </Link>
                    )}
                </div>
                <StatusBadge status={unit.status} />
            </div>

            <Tabs value={tab} onValueChange={setTab} className="space-y-6">
                <TabsList data-testid="unit-tabs">
                    <TabsTrigger value="overview" data-testid="unit-tab-overview">Übersicht</TabsTrigger>
                    <TabsTrigger value="wiki" data-testid="unit-tab-wiki">
                        <BookOpen size={13} className="mr-1.5" /> Wiki
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="overview">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        <div className="lg:col-span-1 bg-white border border-gray-200 rounded-md p-5">
                            <div className="text-[11px] uppercase tracking-[0.18em] text-gray-500 mb-3">Stammdaten</div>
                            <KV icon={Home} label="Typ" value={statusLabel(unit.typ)} />
                            <KV icon={Ruler} label="Fläche" value={unit.size_sqm ? `${unit.size_sqm} m²` : null} />
                            <KV label="Zimmer" value={unit.rooms} />
                            {unit.ausstattung && <KV label="Ausstattung" value={unit.ausstattung} />}
                            {unit.branche && <KV label="Branche" value={unit.branche} />}
                            {unit.stellplatz_nummer && <KV label="Nummer" value={unit.stellplatz_nummer} />}
                            <KV
                                icon={Landmark}
                                label="Grundbuch"
                                value={unit.eigenes_grundbuch
                                    ? (unit.grundbuch_eintrag || "Eigenes Grundbuch (Sondereigentum)")
                                    : `Kein eigenes Grundbuch${unit.nutzungsart ? ` · ${statusLabel(unit.nutzungsart)}` : ""}`}
                            />
                            {unit.iban && <KV label="IBAN (Einheit)" value={unit.iban} />}
                            {/* Besitz- und Eigentumsdaten stehen am Objekt (06.10.2026, O10 = A):
                                die Einheit erbt sie, zeigt sie hier nicht doppelt. */}
                            {unit.notes && <KV label="Notizen" value={unit.notes} />}
                        </div>

                        <div className="lg:col-span-2 space-y-6">
                            <Mietverhaeltnis
                                mieter={aktuell}
                                verlauf={verlauf}
                                gesperrt={gesperrt}
                                grund={grund}
                                onAnpassen={() => setAnpassungOffen(true)}
                                onBearbeiten={() => setMieteOffen(true)}
                            />

                            <div className="bg-white border border-gray-200 rounded-md">
                                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
                                    <div className="font-heading font-semibold text-slate-900">Mieterhistorie</div>
                                    <span className="text-xs text-gray-500">{tenants.length} Einträge</span>
                                </div>
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead className="bg-gray-50 text-[11px] uppercase tracking-[0.12em] text-gray-500">
                                            <tr>
                                                <th className="text-left font-medium px-5 py-2.5">Mieter</th>
                                                <th className="text-left font-medium px-5 py-2.5">Status</th>
                                                <th className="text-left font-medium px-5 py-2.5">Einzug</th>
                                                <th className="text-left font-medium px-5 py-2.5">Auszug</th>
                                                <th className="text-left font-medium px-5 py-2.5" title="Betrag am Mietverhaeltnis -- was heute gilt, steht oben">Vertragsmiete</th>
                                                <th className="text-left font-medium px-5 py-2.5">Kaution</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100">
                                            {tenants.map((t) => (
                                                <tr key={t.id}
                                                    className={`hover:bg-gray-50 ${t.id === aktuell?.id ? "bg-slate-50/60" : ""}`}
                                                    data-testid={`unit-tenant-row-${t.id}`}>
                                                    <td className="px-5 py-3 font-medium text-slate-900">
                                                        {t.full_name}
                                                        {t.id === aktuell?.id && (
                                                            <span className="ml-2 text-[10px] uppercase tracking-wider text-emerald-700">
                                                                aktuell
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className="px-5 py-3"><StatusBadge status={t.status} /></td>
                                                    <td className="px-5 py-3 text-gray-500 tabular-nums">{germanDate(t.einzugsdatum)}</td>
                                                    <td className="px-5 py-3 text-gray-500 tabular-nums">{t.auszugsdatum ? germanDate(t.auszugsdatum) : "—"}</td>
                                                    {/* Vorher stand hier nur die Kaltmiete -- neben der
                                                        Warmmiete in jeder anderen Liste sah das wie ein
                                                        Widerspruch aus. */}
                                                    <td className="px-5 py-3 text-gray-700 tabular-nums">
                                                        {euro(sollAus(t))}
                                                        <div className="text-[10px] text-gray-400">
                                                            {t.mietart === "pauschal" ? "Pauschal" : "kalt + NK"}
                                                        </div>
                                                    </td>
                                                    <td className="px-5 py-3 text-gray-700 tabular-nums">{euro(t.kaution)}</td>
                                                </tr>
                                            ))}
                                            {tenants.length === 0 && (
                                                <tr><td colSpan={6} className="px-5 py-6 text-center text-gray-400 text-sm">Keine Mieter hinterlegt.</td></tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            <div className="bg-white border border-gray-200 rounded-md">
                                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
                                    <div className="font-heading font-semibold text-slate-900">Mieterwechsel</div>
                                    <span className="text-xs text-gray-500">{turnovers.length} Vorgänge</span>
                                </div>
                                <div className="divide-y divide-gray-100">
                                    {turnovers.map((t) => (
                                        <Link key={t.id} to={`/wechsel/${t.id}`} className="flex items-center justify-between px-5 py-3 hover:bg-gray-50" data-testid={`unit-turnover-${t.id}`}>
                                            <div>
                                                <div className="text-sm font-medium text-slate-900">Phase {t.current_phase} · {t.status.replace("_", " ")}</div>
                                                <div className="text-xs text-gray-500">Aktualisiert: {germanDate(t.updated_at)}</div>
                                            </div>
                                            <StatusBadge status={t.status} />
                                        </Link>
                                    ))}
                                    {turnovers.length === 0 && (
                                        <div className="px-5 py-6 text-center text-gray-400 text-sm">Keine aktiven Mieterwechsel.</div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </TabsContent>

                <TabsContent value="wiki">
                    {property ? (
                        <div className="space-y-4">
                            <div className="rounded-md bg-sky-50 border border-sky-200 px-4 py-2.5 text-xs text-sky-900 flex items-center gap-2" data-testid="unit-wiki-hint">
                                <BookOpen size={13} />
                                Einträge gelten für die gesamte Immobilie <b>{property.label || property.address}</b> — alle Einheiten dieses Objekts teilen sich dieses Wiki.
                            </div>
                            <WikiPanel
                                scope="property"
                                scopeId={property.id}
                                scopeLabel={property.label || property.address}
                                highlightEntry={highlightEntry}
                            />
                        </div>
                    ) : (
                        <div className="p-10 text-center text-gray-400 text-sm">Keine Immobilie zugeordnet.</div>
                    )}
                </TabsContent>
            </Tabs>

            <MietanpassungDialog
                mieter={aktuell}
                open={anpassungOffen && !!aktuell}
                onOpenChange={setAnpassungOffen}
                onDone={load}
                gesperrt={gesperrt}
                grund={grund}
            />
            <MieteDialog
                mieter={aktuell}
                open={mieteOffen && !!aktuell}
                onOpenChange={setMieteOffen}
                onDone={load}
            />
        </div>
    );
};

export default UnitDetail;
